import React from "react";
import { useRecoilValue } from 'recoil';
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, Typography, TextField, Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import { isDebug } from "../../utils";
import { userInfo } from "../../auth/AuthProvider";
import { useBoardManageItemQuery } from "../../hooks/useBoard/useBoardManageQuery";
import { useBoardItemListQuery } from "../../hooks/useBoard/useBoardQuery";

import ContentLayout from "../../layouts/ContentLayout";
import { ControlWriteArticle, ControlDataGrid, ControlSnackbar, Columns4Board, ControlCircularProgressBar, Dialog4Confirm, Dialog4Alert, UserType2Value } from "../../components/board";

/*
    2025.04.02
    자유게시판 목록 - ArticleList, QnaList props 하나로 만들수 있으나 확장성을 위해서 두개 파일로 구성
    게시판 정보
    ManageId, BoardName, BoardType, NewYn, NewTerm, CommentYn, WriteRole, ReadRole, OrdYn, CreatedDate, CreatedBy
    게시글 정보
    BoardId, BoardTitle, BoardType, Content, ReplyBody, BoardHit, ManageId, BoardOrd, CreatedDate, CreateByName, CreatedBy
    Comment 정보
    CommentId, BoardId, CreatedByName, CommentBody, CreatedDate, CreatedBy

    CreatedBy 정보와 사용자 계정 정보 같으면 수정 및 삭제 가능 (FAQ 게시판을 제외한 게시판의 경우 적용된다.)
    QnA의 경우 답변이 있으면 수정 및 삭제를 할 수 없다.
    게시글 목록과 보기는 페이지로 구성하고 글 작성은 팝업으로 구성한다.
    게시판은 테이블 목록 형식으로 출력한다.
    조회의 기본은 ManageId, Page, Size를 기본으로 전달한다.

    paramQueryOptions - 뷰페이지에서 목록으로 올때 기존의 검색 정보를 복원한다.
                        기존 검색 정보는 그리드에 queryOptions를 props로 전달고하고 뷰로는 navigate로 전달한다.
*/


export default function ArticleList () 
{
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useRecoilValue(userInfo);
    const { t } = useTranslation();
    let paramQueryOptions = { ...location.state }
    const isLoadingRef = React.useRef(false);
    const refConfirm = React.useRef(null);
    const refAlert = React.useRef(null);
    const refSnackbar = React.useRef(null);
    const showTimerControler = React.useRef(null);

    const [ pageTitle ] = React.useState(t('nav.board3'));
    const defaultBoardInfo = {ManageId: process.env.REACT_APP_BOARD_FREE_MANAGE_ID, BoardName: null, BoardType: null, NewYn: "N", NewTerm: "7", CommentYn: "N", WriteRole: "system", ReadRole: "system", OrdYn: "N" }
    const [ boardInfo, setBoardInfo] = React.useState(defaultBoardInfo);
    const { error: boardInfoError, data: boardInfoData } = useBoardManageItemQuery(process.env.REACT_APP_BOARD_FREE_MANAGE_ID);

    const [paginationModel, setPaginationModel] = React.useState({
        page: paramQueryOptions?.Page ? paramQueryOptions.Page - 1 : 0,
        pageSize: paramQueryOptions?.PageSize ?? 10,
    });
    const defaultQueryOptions = {
        ManageId: paramQueryOptions?.ManageId ? paramQueryOptions.ManageId : process.env.REACT_APP_BOARD_FREE_MANAGE_ID,
        Page: paginationModel.page + 1,
        PageSize: paginationModel.pageSize,
        Title: paramQueryOptions?.Title ? paramQueryOptions.Title : "",
        Content: paramQueryOptions?.Content ? paramQueryOptions.Content : "",
    };
    const [queryOptions, setQueryOptions] = React.useState(defaultQueryOptions);
    const { isError, isLoading, isFetching, isSuccess, error, data: gridData, refetch } = useBoardItemListQuery(queryOptions);

    const [ dataSet, setDataSet ] = React.useState({
        records: [],
        rowLength: 0,
        editable: false,
        isLoading: false,
    });
    const [openEditDialog, setOpenEditDialog] = React.useState(false); // 등록 창
    const boardType = boardInfo.BoardType ?? "board";
    

    const runSearch = (e) => {
        e.preventDefault();
        
        if (isLoadingRef.current)  return;
        isLoadingRef.current = true;
        showTimerControler.current.showTimer(2);

        if(isDebug()) refSnackbar.current.openMessage(JSON.stringify(queryOptions) + ':: 검색');
        refetch();

        isLoadingRef.current = false;
    };

    const callRegistration = (e) => {
        e.preventDefault();

        if(auth.userId === undefined || auth.userId === null || auth.userId === '') {
            refSnackbar.current.openMessage(`${t('error.notlogin')}`);
            setTimeout(() => {
                navigate(`/login`);
            }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
        }
        else {
            if (UserType2Value(boardInfo?.WriteRole || 9) > UserType2Value(auth?.userType || 0)) {
                refAlert.current.showMessageBoxResult(t('error.title'), t('error.notrole'), () => {
                    navigate('/intro');
                });
                return;
            }

            if (isLoadingRef.current)  return;
            isLoadingRef.current = true;
            setOpenEditDialog(true);
            isLoadingRef.current = false;
        }
    };

    const closeEditDialog = (boolValue) => {
        if(boolValue) {
            refetch();
        }
        if(openEditDialog) setOpenEditDialog(false);
    };


    React.useEffect(() => {
        document.title = 'BWOB - ' + pageTitle;
        try {
            if(boardInfoData !== undefined && boardInfoData !== null)
            {
                if(boardInfoData.data.BoardType !== "board")
                {
                    navigate('/intro');
                }
                setBoardInfo(boardInfoData.data);
            }
        }
        catch(err)
        {
            console.log('Access Deny', new Date().toISOString(), boardType, boardInfoData, err);
        }

        return () => {
            setBoardInfo(defaultBoardInfo);
        } 
    }, [boardInfoError, boardInfoData]);

    React.useEffect(() => {
        try {
            if(gridData !== undefined && gridData !== null)
            {
                setDataSet({...dataSet, records: gridData.data.Results, rowLength: gridData.data.TotalCnt, isLoading: false});
            }
        }
        catch(err)
        {
            console.log('No Data', new Date().toISOString(), boardType, queryOptions, err);
        }

        return () => {
        } 
    }, [isError, isLoading, isFetching, isSuccess, error, gridData]);

    React.useEffect(() => { 
        if (isDebug()) console.log("## UseEffect [paginationModel] paginationModel: ", new Date().toISOString(), boardType, JSON.stringify(paginationModel));
        setQueryOptions({...queryOptions, Page: paginationModel.page + 1, PageSize: paginationModel.pageSize});
        paramQueryOptions = queryOptions;
        if (isDebug()) console.log("## UseEffect [paginationModel] queryOptions: ", new Date().toISOString(), boardType, JSON.stringify(queryOptions));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel]);


    return(
        <ContentLayout pageTitle={pageTitle} pageDescription={t('nav.board3_description')} addClassName="content-layout-sub2">

            {/* 검색 영역 */}
            <Container className="page-content" sx={{ p: 1 }} >
                <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ paddingRight: '10px'}} >
                        <TextField
                            fullWidth
                            label={t('searchfield.subject')}
                            id="filled-hidden-label-small"
                            defaultValue={queryOptions.Title}
                            onChange={(v) => setQueryOptions({...queryOptions, Title:v.target.value}) }
                            size="small"
                            placeholder={t('searchfield.subject_placeholder')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ paddingRight: '10px'}} >
                        <TextField
                            fullWidth
                            label={t('searchfield.content')}
                            id="filled-hidden-label-small"
                            defaultValue={queryOptions.Content}
                            onChange={(v) => setQueryOptions({...queryOptions, Content:v.target.value}) }
                            size="small"
                            placeholder={t('searchfield.content_placeholder')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ textAlign: 'right' }} >
                        <div>
                            <Button variant="outlined" size="medium" startIcon={<SearchIcon />} onClick={runSearch}>
                                {t('button.search')}
                            </Button>
                            {' '}
                            <Button variant="outlined" size="medium" startIcon={<AddCircleIcon />} onClick={callRegistration} disabled={!auth.userId}>
                                {t('button.registration')}
                            </Button>
                        </div>
                    </Grid>
                </Grid>
            </Container>

            {/* 데이타 영역 */}
            <Container sx={{ width: '100%', height: '100%', alignItems: 'center', p: 1 }}>
                <ControlDataGrid t={t}
                    dataType="board"
                    manageId={queryOptions.ManageId}
                    boardColumns={Columns4Board}
                    dataSet={dataSet}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    isUseManager={false}
                    queryOptions={queryOptions}
                />
            </Container>

            {/* 알림 메시지 영역 */}
            <Dialog4Alert ref={refAlert} />
            <Dialog4Confirm ref={refConfirm} />
            <ControlSnackbar ref={refSnackbar} />
            <ControlCircularProgressBar ref={showTimerControler} initialTime={1} />
            <Typography>&nbsp;</Typography>

            {/* 게시글 등록 영역 */}
            <ControlWriteArticle id="writeArticle" open={openEditDialog} closeEditDialog={closeEditDialog} manageId={process.env.REACT_APP_BOARD_FREE_MANAGE_ID} refMessage={showTimerControler.current} />

        </ContentLayout>
    );

};